import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchApi } from '../services/HTTP';
import { logout } from './authSlice';
import {hideLoader, showLoader} from './rootManagerSlice';
import {CREATE_EVENT_MODAL, updateModalRes} from "./modalManagerSlice";
import { flowOptions } from '../components/Structure/Schedule/Popups/CreateEvent/FlowOptions';
import {t} from "../services/i18n";
import {getScheduleCustomFields} from "./ScheduleSlice";
import {SCHEDULE_TYPE} from "../services/ConstHelper";
import _ from "lodash";
import {sendMessageToAngular} from "../services/IframeIntegrationServices";
import {Colors} from "../styles/Colors";
import {ArrivalStatuses} from "../Configs/DatabaseConsts";
import {getDefaultArrivalStatuses} from "../services/Helpers";

const boxSlice = createSlice({
  name: "box",
  initialState: {
    loading: false,
    box: { counters: { tasks: 0, finance: 0 } },
    error: '',
  },
  reducers: {
    setBox: (state, action) => {
      state.box = Object.assign(state.box, action.payload);
    },
    setCounters: (state, action) => {
      state.box.counters = action.payload;
    },
    setReportsSettings: (state, action) => {
      state.box.reportsSettings = action.payload;
    },
    updateBox: (state, action) => {
      state.box = { ...state.box, ...action.payload }
    },
    resetIncomeCards: (state, action) => {
      if (!state.box.incomeCardsByLocationId) {
        return;
      }
      const locationId = action.payload;
      if (locationId) {
        delete state.box.incomeCardsByLocationId[getIncomeCardsCacheKey(locationId)];
      } else {
        state.box.incomeCardsByLocationId = {};
      }
    },
    updateAllLocations: (state, action) => {
      const newLocations = state.box.locations_box.reduce((acc, location) => [...acc, {...location, ...action.payload}], [])
        state.box = {...state.box, locations_box: newLocations}
    },
    updateLocations: (state, action) => {
      state.box = {...state.box, locations_box: action.payload}
    },
    updateTaskTypes: (state, action) => {
      state.box.task_types = action.payload
    },
    updateAutoTasks: (state, action) => {
      state.box.auto_tasks = action.payload
    },
    updateMessageTemplates: (state, action) => {
      const messageTemplateToUpdate = action.payload
      if(!state.box.message_template) {
        state.box.message_template = [messageTemplateToUpdate]
        return;
      }

      const messageTemplateToUpdateIndex = state.box.message_template.findIndex(item => item.id === messageTemplateToUpdate.id)

      if(messageTemplateToUpdateIndex !== -1) {
        state.box.message_template[messageTemplateToUpdateIndex] = messageTemplateToUpdate
      } else {
        state.box.message_template = [...state.box.message_template, messageTemplateToUpdate]
      }
    },
    removeCalendarSyncConnection: (state, action) => {
      const usersBoxesId = action.payload;
      if (Array.isArray(state.box.calendarSync)) {
        state.box.calendarSync = state.box.calendarSync.filter(item => item.users_boxes_id !== usersBoxesId);
      } else {
        state.box.calendarSync = null;
      }
    },
  },
  extraReducers: builder => {
    /** LEAD STATUS */
    builder.addCase(saveLeadStatus.pending, (state) => {
      state.loading = true
    })
    builder.addCase(saveLeadStatus.fulfilled, (state, data) => {
      state.loading = false
      state.error = ''
      state.box.box_statuses = [ ...state.box.box_statuses, {...data.payload, active: 1} ]
    })
    builder.addCase(saveLeadStatus.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    /** LEAD SOURCE */
    builder.addCase(saveLeadSource.pending, (state) => {
      state.loading = true
    })
    builder.addCase(saveLeadSource.fulfilled, (state, data) => {
      state.loading = false
      state.error = ''
      state.box.box_sources = [ ...state.box.box_sources, {...data.payload, active: 1} ]
    })
    builder.addCase(saveLeadSource.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    builder.addCase(saveTaskType.pending, (state) => {
      state.loading = true
    })
    builder.addCase(saveTaskType.fulfilled, (state, data) => {
      state.loading = false
      state.error = ''
      const type = state.box.task_types.find(type => type.id === data.payload.id)
      state.box.task_types = type ? [...state.box.task_types.map(taskType => taskType.id === data.payload.id ? data.payload : taskType)] : [...state.box.task_types, {...data.payload, active: 1}]

    })
    builder.addCase(saveTaskType.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    /** LOST LEAD REASON */
    builder.addCase(saveLostLeadReason.pending, (state) => {
      state.loading = true
    })
    builder.addCase(saveLostLeadReason.fulfilled, (state, data) => {
      state.loading = false
      state.error = ''
      state.box.lead_lost_reasons = [ ...state.box.lead_lost_reasons, {...data.payload, active: 1} ]
    })
    builder.addCase(saveLostLeadReason.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })

    /** REPORTS SETTINGS */
    builder.addCase(saveReportsSettings.pending, (state, action) => {
      const {reportName} = action.meta.arg
      state.box.loadingReports = {...(state.box.loadingReports || {}), [reportName]: true}
    })
    builder.addCase(saveReportsSettings.fulfilled, (state, data) => {
      state.box.loadingReports = {...(state.box.loadingReports || {}), [data.payload.reportName]: false}
      state.error = ''
      state.box.reportsSettings = data.payload
    })
    builder.addCase(saveReportsSettings.rejected, (state, data) => {
      state.box.loadingReports = {...(state.box.loadingReports || {}), [data.payload.reportName]: false}
      state.error = data.payload?.error
    })
    /** GET BOX DATA */
    builder.addCase(getBox.pending, (state) => {
      state.loading = true
    })
    builder.addCase(getBox.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
      state.box = Object.assign(state.box, action.payload);
      delete state.box.locationsByRivhitId;
    })
    builder.addCase(getBox.rejected, (state, action) => {
      console.log('getBox error', action);
      state.loading = false
      state.error = action.payload?.error
      state.box = { counters: { tasks: 0, finance: 0 } }
    })
    builder.addCase(isBoxHasNewSite.pending, (state) => {
      state.loading = true
    })
    builder.addCase(isBoxHasNewSite.fulfilled, (state, data) => {
      state.loading = false
      state.box.isBoxHasNewSite = !data.payload
    })
    builder.addCase(isBoxHasNewSite.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    builder.addCase(getAutoActions.pending, (state) => {
      state.loading = true
    })
    builder.addCase(getAutoActions.fulfilled, (state, data) => {
      state.loading = false
      state.error = ''
      state.box.auto_actions = data.payload
      state.box.auto_tasks = data.payload.filter(item => item.task_type_id).map(item => {
        if (item.case === 'IMMEDIATE') {
          item.timeStr = t('immediateAutomaticAction')
          item.hourStr = '';
        } else {
          let timeStr = item.period_amount + ' ' + (item?.auto_actions?.action === 'CLASS_END' ? t('classes') + ' ' : t(item.period_time_unit + 'automaticActionTable') + ' ');
          let str = '';
          if (item.before_after === 'AFTER') {
            str = t('afterAutomticActionTable') + ' ' + timeStr;
          } else {
            str = timeStr + t('before');
          }
          item.timeStr = item.period_amount === 0 ? t('immediateAutomaticAction') : str;
          item.hourStr = item.hour
              ? item.hour.split(':').slice(0, -1).join(':')
              : null;
        }
        return item
      })

    })
    builder.addCase(getAutoActions.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    builder.addCase(checkBoxFreeFitPermission.fulfilled, (state, data) => {
      state.loading = false
      state.box.hasFreeFitPermission = data.payload.length > 0 ? true : false
    })
    builder.addCase(checkBoxFreeFitPermission.pending, (state, data) => {
      state.loading = true
    })
    builder.addCase(checkBoxFreeFitPermission.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    builder.addCase(checkBoxPermissionWithoutLock.fulfilled, (state, data) => {
      state.loading = false
      state.box = {...state.box, ...data.payload}
    })
    builder.addCase(checkBoxPermissionWithoutLock.pending, (state, data) => {
      state.loading = true
    })
    builder.addCase(checkBoxPermissionWithoutLock.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    builder.addCase(getReportsCustomFieldsByFlowName.pending, (state) => {
      state.loading = true
    })
    builder.addCase(getReportsCustomFieldsByFlowName.fulfilled, (state, data) => {
      state.loading = false
      state.box.reportsCustomFields = data.payload
    })
    builder.addCase(getReportsCustomFieldsByFlowName.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    builder.addCase(getBoxCancelReasons.fulfilled, (state, action) => {
      state.box.boxCancelReasons = action.payload
      state.error = ''
    })
    builder.addCase(getBoxCancelReasons.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getScheduleCancelReasons.fulfilled, (state, action) => {
      state.box.scheduleCancelReasons = action.payload
      state.error = ''
    })
    builder.addCase(getScheduleCancelReasons.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getBoxSettings.fulfilled, (state, data) => {
      state.loading = false
      state.error = ''
      state.box.settings = data.payload
    })
    builder.addCase(getBoxSettings.rejected, (state, data) => {
      state.loading = false
      state.error = data.payload?.error
    })
    builder.addCase(getBoxSuspendReasons.fulfilled, (state, action) => {
      state.box.boxSuspendReasons = action.payload
      state.error = ''
    })
    builder.addCase(getBoxSuspendReasons.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getBoxArrivalStatuses.fulfilled, (state, action) => {
      state.box.arrivalStatuses = action.payload
      state.error = ''
    })
    builder.addCase(getBoxArrivalStatuses.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getCalendarSync.fulfilled, (state, action) => {
      state.box.calendarSync = action.payload
      state.error = ''
    })
    builder.addCase(getCalendarSync.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getPromoCodes.fulfilled, (state, action) => {
      state.box.promoCodes = action.payload
      state.error = ''
    })
    builder.addCase(getPromoCodes.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getLocationsByRivhitId.fulfilled, (state, action) => {
      state.box.locationsByRivhitId = action.payload
      state.error = ''
    })
    builder.addCase(getLocationsByRivhitId.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getIncomeCards.fulfilled, (state, action) => {
      const {cacheKey, cards} = action.payload;
      state.box.incomeCardsByLocationId = {
        ...(state.box.incomeCardsByLocationId || {}),
        [cacheKey]: cards,
      };
      state.error = '';
    })
    builder.addCase(getIncomeCards.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
    builder.addCase(getTrackDepartments.fulfilled, (state, action) => {
      state.box.activeDepartments = action.payload
      state.error = ''
    })
    builder.addCase(getTrackDepartments.rejected, (state, action) => {
      state.error = action.payload?.error;
    })
  }
});

export const { setBox, setCounters, updateBox, updateAllLocations, updateLocations, setReportsSettings, updateTaskTypes, updateAutoTasks, updateMessageTemplates, removeCalendarSyncConnection, resetIncomeCards } = boxSlice.actions;

export default boxSlice.reducer;


export const saveLeadStatus = createAsyncThunk('saveLeadStatus', async (params, { rejectWithValue }) => {
  return await fetchApi('boxes/leadStatus', 'POST', params, false, true);
})

export const saveLostLeadReason = createAsyncThunk('saveLostLeadReason', async (params, { rejectedWithValue }) => {
  return await fetchApi('boxes/lostLeadReason', 'POST', params, false, true);
})

export const saveLeadSource = createAsyncThunk('saveLeadSource', async (params, { rejectWithValue }) => {
  return await fetchApi('boxes/leadSource', 'POST', params, false, true);
})

export const saveTaskType = createAsyncThunk('saveTaskType', async (params, { rejectWithValue, dispatch, getState }) => {
  dispatch(showLoader())
  const res = await fetchApi('boxes/taskType', 'POST', params, false, true);
  dispatch(hideLoader());
  if(getState().modalManager.createEventModal.isOpen) {
    dispatch(updateModalRes({modalName: CREATE_EVENT_MODAL, res: {res, flowName: flowOptions.TASK_TYPE_FLOW}}))
  }
  return res
})

export const saveReportsSettings = createAsyncThunk('saveReportsSettings', async (params, { rejectWithValue, dispatch }) => {
 await fetchApi('reports/settings', 'POST', params, false, true);
  return params;
})

export const getBox = createAsyncThunk('getBox', async (params, { rejectWithValue, dispatch, getState }) => {
  try {
    const payload = await fetchApi('getBoxData', 'GET', null, false, true);
    const user = getState()?.user?.user;
    if (user?.network_management && Array.isArray(user?.allowed_locations) && payload?.locations_box) {
      payload.all_locations_box = payload.locations_box;
      payload.locations_box = payload.locations_box.filter(location =>
        user.allowed_locations.some(id => Number(id) === Number(location.id))
      );
    }
    return payload;
  } catch (e) {
    console.log('getBox error', e);
    if (e?.statusCode == 401) {
      dispatch(hideLoader());
      dispatch(logout());
    }
    return rejectWithValue(e);
  }
})

export const isBoxHasNewSite = createAsyncThunk('isBoxHasNewSite', async (params, { rejectedWithValue,dispatch }) => {
  return await fetchApi(`site/boxes/isFirstPay/${params.identifier}`, 'GET', null, true, true)

})

export const getAutoActions = createAsyncThunk('getAutoActions', async  (params, { rejectedWithValue }) => {
  return await fetchApi(`autoActionsSet/getAutoActionsSetByBoxId`, 'POST', {}, false, true)
})

export const checkBoxFreeFitPermission = createAsyncThunk('CheckBoxFreeFitPermission', async  (params, { rejectedWithValue }) => {
  return await fetchApi(`boxes/checkBoxFreeFitPermission`, 'POST', {}, false, true)

})

export const checkBoxPermissionWithoutLock = createAsyncThunk('checkBoxPermissionWithoutLock', async  (params, { rejectedWithValue }) => {
  return await fetchApi(`boxes/checkBoxPermissionWithoutLock`, 'POST', {permission: 'availability'}, false, true)

})

export const getBoxSettings = createAsyncThunk('getBoxSettings', async (params, { rejectedWithValue }) => {
  const res = await fetchApi(`boxes/settings`, 'GET', {}, false, true)
  sendMessageToAngular('box_settings', {settings: res}, 'main_angular_frame_id');
  return res
})

export const setBoxSettings = createAsyncThunk('setBoxSettings', async (params, { rejectedWithValue }) => {
  return await fetchApi(`boxes/settings`, 'POST', params, false, true)
})


export const getBoxCancelReasons = createAsyncThunk('getBoxCancelReasons', async  (params, { rejectedWithValue }) => {
  return await fetchApi(`getBoxCancelReasons`, 'POST', null, false, true)
})
export const getScheduleCancelReasons = createAsyncThunk('getScheduleCancelReasons', async  (params, { rejectedWithValue }) => {
  return await fetchApi(`getBoxCancelReasons`, 'POST', {scope: 'schedule'}, false, true)
})

export const getBoxSuspendReasons = createAsyncThunk('getBoxSuspendReasons', async  (params, { rejectedWithValue }) => {
  return await fetchApi(`getBoxSuspendReasons`, 'POST', null, false, true)
})

export const getBoxArrivalStatuses = createAsyncThunk('getBoxArrivalStatuses', async  (params, { rejectedWithValue }) => {
  let res = await fetchApi('getArrivalStatusesOfBox', 'POST', null, false, true);
  const confirmedStatus = res.find(arrivalStatus => arrivalStatus.status === ArrivalStatuses.CONFIRMED)
  return getDefaultArrivalStatuses(confirmedStatus)
})

export const getCalendarSync = createAsyncThunk('getCalendarSync', async  (params, { rejectedWithValue }) => {
  return await fetchApi('calendarSyncConnections/getCalendarSync', 'POST', null, false, true)
})

export const getPromoCodes = createAsyncThunk('getPromoCodes', async (params, { rejectedWithValue }) => {
  return await fetchApi('promoCode/getAll', 'GET', null, false, true)
})

export const getLocationsByRivhitId = createAsyncThunk('getLocationsByRivhitId', async (params, { rejectWithValue }) => {
  try {
    return await fetchApi('locationsBox/getLocationByBoxId', 'POST', {}, false, true);
  } catch (e) {
    return rejectWithValue(e);
  }
})

export const getTrackDepartments = createAsyncThunk('getTrackDepartments', async (params, { rejectWithValue }) => {
  try {
    return await fetchApi('getDepartments', 'POST', {}, false, true);
  } catch (e) {
    return rejectWithValue(e);
  }
})

export const getIncomeCardsCacheKey = (locationId) => (locationId ?? 'default').toString();

export const getIncomeCards = createAsyncThunk(
  'getIncomeCards',
  async (locationId) => {
    try {
      const query = locationId ? `?locationId=${locationId}` : '';
      const res = await fetchApi(`finance/incomeCards${query}`, 'GET', null, false, true);
      const cards = (res.cards || []).map((card) => ({
        value: card.id,
        label: card.name,
      }));
      return {cacheKey: getIncomeCardsCacheKey(locationId), cards};
    } catch (e) {
      return {
        cacheKey: getIncomeCardsCacheKey(locationId),
        cards: [{value: null, label: t('no-income-card')}],
      };
    }
  },
  {
    condition: (locationId, { getState }) => {
      const cacheKey = getIncomeCardsCacheKey(locationId);
      return !getState().box.box.incomeCardsByLocationId?.[cacheKey];
    },
  },
);

export const getLocationsByRivhitIdOptions = (locationsByRivhitId, { valueAsArray = false, includeAllLocations = false } = {}) => {
  if (!locationsByRivhitId?.length) return [];
  const grouped = _.groupBy(locationsByRivhitId, 'rivhitIdGroup');
  const result = _.map(grouped, (items) => ({
    label: _.map(items, 'location').join(', '),
    value: valueAsArray ? _.map(items, 'id') : items[0].id,
  }));
  return includeAllLocations ? [{ value: -1, label: t('allLocations') }, ...result] : result;
}

export const getLocationsInSameRivhitGroup = (locationsByRivhitId, locationId) => {
  if (!locationsByRivhitId?.length || !locationId) return null;
  const selectedLocation = locationsByRivhitId.find((location) => location.id == locationId);
  if (!selectedLocation) return null;
  return locationsByRivhitId.filter((location) => location.rivhitIdGroup === selectedLocation.rivhitIdGroup);
}

export const getReportsCustomFieldsByFlowName = createAsyncThunk('getReportsCustomFieldsByFlowName', async  (params, {rejectedWithValue}) => {
  const res = await fetchApi(`customField/getCustomFieldByFlowNameOrType`, 'POST', {getAllLocationsBox: true}, false, true)
  return res.reduce((acc, item) => {
    item.custom_field_flow?.forEach(flowItem => {
      const flowName = flowItem.flow?.name;
      if (flowName) {
        if (!acc[flowName]) {
          acc[flowName] = [];
        }
        acc[flowName].push(item);
      }
    });

    return acc;
  }, {});
})